import type { ComponentPropsWithoutRef } from 'react';

import { Link } from 'react-router-dom';

import { cn } from '@shared/lib/utils';
import { buttonVariants } from '@shared/ui/button';
import { CortexaLogo } from '@shared/ui/cortexa-logo';

const legalParagraphs = [
  'Cortexa provides planning, practice, and application workflow support. Features, availability, and recommendations may vary by program, geography, and subscription tier.',
  'School requirements, testing policies, and submission deadlines can change. Applicants should always confirm final instructions and eligibility details with each institution directly.',
  'AI-generated suggestions are intended to support preparation and drafting. Final essays, resumes, and other application materials remain the applicant’s responsibility.',
  'By using Cortexa, you agree to the applicable terms, privacy notices, and product policies that govern beta features, integrations, communications, and account access.',
];

const socialIconPaths = {
  x: 'M17.1761 4H19.9362L13.9061 10.7774L21 20H15.4456L11.0951 14.4066L6.11723 20H3.35544L9.80517 12.7508L3 4H8.69545L12.6279 9.11262L17.1761 4ZM16.2073 18.3754H17.7368L7.86441 5.53928H6.2232L16.2073 18.3754Z',
  linkedIn:
    'M12.9444 19H9.16665V7.70588H12.9444V9.58824C13.7497 8.5673 14.9708 7.95866 16.2736 7.92894C18.6164 7.9419 20.5068 9.84177 20.5 12.1765V19H16.7222V12.6471C16.5711 11.5954 15.6661 10.8151 14.6 10.8174C14.1337 10.8321 13.6936 11.0356 13.3812 11.3808C13.0687 11.7261 12.911 12.1833 12.9444 12.6471V19ZM7.27777 19H3.5V7.70588H7.27777V19ZM5.38888 6.76471C4.34568 6.76471 3.5 5.92195 3.5 4.88235C3.5 3.84276 4.34568 3 5.38888 3C6.43209 3 7.27777 3.84276 7.27777 4.88235C7.27777 5.38158 7.07876 5.86037 6.72453 6.21338C6.37029 6.56639 5.88985 6.76471 5.38888 6.76471Z',
} as const;

const socialLinks = [
  {
    href: 'https://www.linkedin.com/',
    label: 'LinkedIn',
    path: socialIconPaths.linkedIn,
  },
  {
    href: 'https://x.com/',
    label: 'X',
    path: socialIconPaths.x,
  },
] as const;

interface SocialIconProps extends ComponentPropsWithoutRef<'svg'> {
  path: string;
}

const SocialIcon = ({ path, className, ...props }: SocialIconProps) => {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={cn('size-6 text-foreground-4', className)}
      {...props}
    >
      <path d={path} fill="currentColor" />
    </svg>
  );
};

export const LandingFooter = () => {
  const currentYear = new Date().getFullYear();

  return (
    <footer className="-mt-10 w-full bg-background-2">
      <div
        className="
          landing-container mx-auto flex w-full max-w-400 flex-col gap-8 pt-20 pb-10
          md:pb-6
          lg:pt-24
        "
      >
        <div
          className="
            flex flex-row items-center justify-center gap-8
            md:justify-between
          "
        >
          <CortexaLogo
            size="lg"
            keepColors={false}
            className="
              -ms-1 text-background-5 drop-shadow-recess
              md:h-14
            "
          />

          <div
            className="
              flex items-center gap-1 text-foreground/70
              max-md:flex-col
            "
          >
            {socialLinks.map((socialLink) => (
              <a
                key={socialLink.label}
                href={socialLink.href}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={socialLink.label}
                className={cn(
                  `
                    delay-50
                    hover:bg-background!
                  `,
                  buttonVariants({ variant: 'ghost', size: 'icon-lg' })
                )}
              >
                <SocialIcon
                  path={socialLink.path}
                  aria-hidden="true"
                  className="
                    text-foreground-6 transition-[inherit] delay-[inherit] duration-[inherit]
                    ease-[inherit]
                    group-hover/button:text-foreground-4
                  "
                />
              </a>
            ))}
          </div>
        </div>

        <div className="border-b border-border pb-8">
          <p className="text-sm/6 text-foreground/40">
            {legalParagraphs.map((paragraph) => (
              <span key={paragraph}> {paragraph} </span>
            ))}
          </p>
        </div>

        <div
          className="
            flex flex-col gap-3 text-sm/5 text-foreground/40
            sm:flex-row sm:items-start sm:justify-between
          "
        >
          <p>© {currentYear} Cortexa Labs. All rights reserved.</p>
          <div
            className="
              flex flex-wrap items-center gap-4
              sm:justify-end
            "
          >
            <Link
              to="/terms-and-privacy"
              className="
                transition-colors duration-200
                hover:text-foreground
              "
            >
              Terms &amp; Privacy
            </Link>
            <Link
              to="/legal"
              className="
                transition-colors duration-200
                hover:text-foreground
              "
            >
              Legal
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
};
